/**
 * 成就系统 API
 * 
 * 提供成就查询、签到、进度查询等功能
 * 
 * @version 1.0.0
 * @author Frontend Team
 * @since 2025-12-11
 */

import { http } from '@/utils/http'
import { unwrap } from '@/utils/apiClient'
import type {
  UserAchievementResponse,
  DailyCheckInResponse,
  AchievementProgress,
  AchievementCategory,
  AchievementTier
} from '@/types/achievement.types'

/**
 * 成就进度查询参数
 */
export interface AchievementProgressQuery {
  /** 成就类别 */
  category?: AchievementCategory
  /** 成就等级 */
  tier?: AchievementTier
  /** 是否只显示已解锁 */
  unlocked?: boolean
}

/**
 * 成就进度响应
 */
export interface AchievementProgressResponse {
  /** 成就列表 */
  achievements: AchievementProgress[]
  /** 总数 */
  total: number
}

/**
 * 获取我的成就概览
 *
 * @returns 用户成就数据
 *
 * @example
 * ```ts
 * const achievements = await getMyAchievements()
 * console.log('当前等级:', achievements.level)
 * console.log('经验值:', achievements.experience)
 * ```
 */
export async function getMyAchievements(): Promise<UserAchievementResponse> {
  return unwrap(() => http.get('/api/v1/achievements'))
}

/**
 * 每日签到
 *
 * @returns 签到结果
 *
 * @example
 * ```ts
 * const result = await dailyCheckIn()
 * if (result.success) {
 *   console.log('签到成功，获得经验:', result.expGained)
 * }
 * ```
 */
export async function dailyCheckIn(): Promise<DailyCheckInResponse> {
  return unwrap(() => http.post('/api/v1/achievements/daily-checkin'))
}

/**
 * 获取成就进度列表
 *
 * @param params 查询参数
 * @returns 成就进度列表
 *
 * @example
 * ```ts
 * // 获取所有使用类成就
 * const usageAchievements = await getAchievementProgress({ category: 'usage' })
 *
 * // 获取已解锁的黄金成就
 * const goldUnlocked = await getAchievementProgress({
 *   tier: 'gold',
 *   unlocked: true
 * })
 * ```
 */
export async function getAchievementProgress(
  params?: AchievementProgressQuery
): Promise<AchievementProgressResponse> {
  return unwrap(() => http.get('/api/v1/achievements/progress', { params }))
}

/**
 * 成就系统 API 导出
 */
export default {
  getMyAchievements,
  dailyCheckIn,
  getAchievementProgress
}

